import React, { createContext, useContext, useEffect, useLayoutEffect, useState } from 'react';
import type { DensityMode, Theme } from '@/components/settings/types/theme';
import { loadFont, preloadFonts } from '@/components/settings/lib/font-loader';

import logger from '@/lib/logging';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
}

export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export interface ThemeProviderProps {
  initialTheme: Theme;
  children: React.ReactNode;
}

const VALID_DENSITIES: DensityMode[] = ['compact', 'comfortable', 'cozy'];

function applyThemeToDocument(theme: Theme) {
  const root = document.documentElement;

  root.style.setProperty('--font-family-base', theme.typography.fontFamily);
  root.style.setProperty('--font-scale', theme.typography.fontScale.toString());

  document.body.style.fontFamily = theme.typography.fontFamily;
  root.style.fontFamily = theme.typography.fontFamily;

  const densityMultiplier = theme.density === 'compact' ? '0.8' : theme.density === 'cozy' ? '1.2' : '1';
  root.style.setProperty('--density-multiplier', densityMultiplier);

  root.classList.remove('density-compact', 'density-comfortable', 'density-cozy');
  root.classList.add(`density-${theme.density}`);
}

/** Strip legacy color/mode fields from stored theme blobs. */
export function normalizeStoredTheme(raw: unknown, fallback: Theme): Theme {
  if (!raw || typeof raw !== 'object') return fallback;
  const stored = raw as Record<string, unknown>;
  const typography = stored.typography;
  const fontFamily =
    typography &&
    typeof typography === 'object' &&
    typeof (typography as Record<string, unknown>).fontFamily === 'string'
      ? (typography as Record<string, unknown>).fontFamily as string
      : fallback.typography.fontFamily;
  const fontScale =
    typography &&
    typeof typography === 'object' &&
    typeof (typography as Record<string, unknown>).fontScale === 'number'
      ? (typography as Record<string, unknown>).fontScale as number
      : fallback.typography.fontScale;
  const density = VALID_DENSITIES.includes(stored.density as DensityMode)
    ? (stored.density as DensityMode)
    : fallback.density;
  const iconStyle = stored.iconStyle === 'solid' ? 'solid' : fallback.iconStyle;
  const name = typeof stored.name === 'string' ? stored.name : fallback.name;
  return {
    name,
    typography: { fontFamily, fontScale },
    iconStyle,
    density,
  };
}

function readEnhancedThemeFromStorage(fallback: Theme): Theme {
  if (typeof window === 'undefined') return fallback;
  try {
    const stored = localStorage.getItem('harry.theme.v2');
    if (!stored) return fallback;
    return normalizeStoredTheme(JSON.parse(stored), fallback);
  } catch (error) {
    logger.warn('Failed to parse enhanced theme from localStorage:', error);
    return fallback;
  }
}

/**
 * Enhanced ThemeProvider that extends existing theme functionality
 *
 * Provides:
 * - CSS variable injection for fonts and density (colors live in globals.css)
 * - Dynamic Google Fonts loading
 * - localStorage persistence
 * - Integration with the basic ThemeProvider (dark/light/high-contrast on `html`)
 *
 * SSR: `initialTheme` is used when storage is absent; seeded E2E `harry.theme.v2` is read synchronously
 * on mount so CSS variables align with `.light`/`.dark` shells before the first layout paint.
 */
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ initialTheme, children }) => {
  const [theme, setTheme] = useState<Theme>(() => readEnhancedThemeFromStorage(initialTheme));
  const [persistReady, setPersistReady] = useState(false);

  // Preload fonts once on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      preloadFonts();
      loadFont(theme.typography.fontFamily).catch((err) => {
        logger.warn('Failed to load initial font:', err);
      });
    }
  }, []);

  useLayoutEffect(() => {
    try {
      const stored = localStorage.getItem('harry.theme.v2');
      if (stored) {
        const parsed = normalizeStoredTheme(JSON.parse(stored), initialTheme);
        setTheme(parsed);
      }
    } catch (error) {
      logger.warn('Failed to read enhanced theme from localStorage:', error);
    }
    // Defer persistence unlock until after layout-driven state updates settle; otherwise the first
    // persist effect can stringify `initialTheme` and overwrite seeded `harry.theme.v2` before
    // `setTheme(parsed)` commits (Playwright/theme-matrix relies on seeded storage surviving).
    queueMicrotask(() => {
      setPersistReady(true);
    });
  }, []);

  useLayoutEffect(() => {
    applyThemeToDocument(theme);
    loadFont(theme.typography.fontFamily).catch((err) => {
      logger.warn('Failed to load theme font:', err);
    });
  }, [theme]);

  useEffect(() => {
    if (!persistReady) return;
    try {
      localStorage.setItem('harry.theme.v2', JSON.stringify(theme));
    } catch (error) {
      logger.warn('Failed to save theme to localStorage:', error);
    }
  }, [persistReady, theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export const defaultTheme: Theme = {
  name: 'harry-default',
  typography: {
    fontFamily: 'Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    fontScale: 1,
  },
  iconStyle: 'outline',
  density: 'comfortable',
};
